import { FeatureFlag } from '../constants/featureFlags';
import { useStore } from '../store';

/** How often a queued or running import is re-read. */
export const IMPORT_POLL_INTERVAL_MS = 5_000;

type ImportAgency =
  | {
      is_kinnser_enabled?: boolean | null;
      is_rogeremr_enabled?: boolean | null;
      system_name?: string | null;
    }
  | null
  | undefined;

/**
 * Whether this agency can have on-demand imports to read: it needs both the
 * External EMR integration (to find the chart) and RogerEMR (to build it).
 *
 * Gates status display only. An import started before the agency's import
 * flag went off still finishes, and its status still shows.
 */
export function canAgencyReadExternalEmrImports(agency: ImportAgency): boolean {
  return (
    agency?.is_kinnser_enabled === true && agency?.is_rogeremr_enabled === true
  );
}

/**
 * Whether this agency can start an on-demand import: the read conditions, the
 * agency's External EMR import flag, and a system name. Gates the Import and
 * retry controls, the census refresh, and every surface that shows census
 * shells. Mirrors the server's `require_external_emr_import_agency`.
 */
export function canAgencyImportFromExternalEmr(
  agency: ImportAgency,
  importFlagOn: boolean
): boolean {
  // Roger Docs keys the agency by its system name; without one nothing
  // can import.
  return (
    canAgencyReadExternalEmrImports(agency) &&
    importFlagOn &&
    (agency?.system_name ?? '').trim() !== ''
  );
}

/** {@link canAgencyImportFromExternalEmr} for the signed-in user's agency. */
export function useCanAgencyImportFromExternalEmr(): boolean {
  const agency = useStore(state => state.user?.agency);
  const importFlagOn = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.EXTERNAL_EMR_IMPORT, 'workos')
  );
  return canAgencyImportFromExternalEmr(agency, importFlagOn);
}

/**
 * Whether this agency has an External EMR: the read conditions and a system
 * name, without the import flag. Mirrors the server's
 * `external_emr_available`. Take Over v2 and its sync review also need the
 * `external-emr-take-over` flag (`useTakeOverAvailable`).
 */
export function agencyHasExternalEmr(agency: ImportAgency): boolean {
  return (
    canAgencyReadExternalEmrImports(agency) &&
    (agency?.system_name ?? '').trim() !== ''
  );
}

/** {@link agencyHasExternalEmr} for the signed-in user's agency. */
export function useAgencyHasExternalEmr(): boolean {
  const agency = useStore(state => state.user?.agency);
  return agencyHasExternalEmr(agency);
}
